import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { formatDate } from '../../lib/utils';
import { Archive, RotateCcw, Search, User, Users, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ArchivedPage = () => {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('participants');
  const [archivedParticipants, setArchivedParticipants] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [restoreType, setRestoreType] = useState('');

  useEffect(() => {
    fetchArchivedData();
  }, []);

  const fetchArchivedData = async () => {
    setLoading(true);
    try {
      const [participantsRes, usersRes] = await Promise.all([
        api.get('/participants/archived'),
        isAdmin ? api.get('/users/archived') : Promise.resolve({ data: [] })
      ]);
      setArchivedParticipants(participantsRes.data);
      setArchivedUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load archived data');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      const endpoint = restoreType === 'participant' 
        ? `/participants/${itemToRestore.id}/restore`
        : `/users/${itemToRestore.id}/restore`;
      
      await api.post(endpoint);
      toast.success(`${restoreType === 'participant' ? 'Participant' : 'User'} restored successfully`);
      setRestoreDialogOpen(false);
      setItemToRestore(null);
      fetchArchivedData();
    } catch (error) {
      toast.error('Failed to restore');
    }
  };

  const openRestoreDialog = (item, type) => {
    setItemToRestore(item);
    setRestoreType(type);
    setRestoreDialogOpen(true);
  };

  const filteredParticipants = archivedParticipants.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.ndis_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = archivedUsers.filter(u => 
    `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Archived Records"
        description="View and restore archived participants and users"
        icon={Archive}
      />

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search archived records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="participants" className="gap-2">
            <Users className="h-4 w-4" />
            Participants ({archivedParticipants.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" className="gap-2">
              <User className="h-4 w-4" />
              Users ({archivedUsers.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="participants" className="mt-4">
          {filteredParticipants.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No archived participants</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredParticipants.map(participant => (
                <Card key={participant.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                          <User className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{participant.first_name} {participant.last_name}</h3>
                            <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                              Archived
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">NDIS: {participant.ndis_number}</p>
                          {participant.house_name && (
                            <p className="text-sm text-muted-foreground">House: {participant.house_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-muted-foreground">Archived {formatDate(participant.archived_at)}</p>
                          {participant.archived_by_name && (
                            <p className="text-muted-foreground">by {participant.archived_by_name}</p>
                          )}
                          {participant.archive_reason && (
                            <p className="text-xs text-muted-foreground italic">"{participant.archive_reason}"</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/participants/${participant.id}`)}
                          >
                            View
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              onClick={() => openRestoreDialog(participant, 'participant')}
                              className="gap-1"
                            >
                              <RotateCcw className="h-4 w-4" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            {filteredUsers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No archived users</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredUsers.map(user => (
                  <Card key={user.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                            <User className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{user.first_name} {user.last_name}</h3>
                              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                                Archived
                              </Badge>
                              <Badge variant="outline">{user.role}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Archived {formatDate(user.archived_at)}</p>
                            {user.archived_by_name && (
                              <p className="text-muted-foreground">by {user.archived_by_name}</p>
                            )}
                            {user.archive_reason && (
                              <p className="text-xs text-muted-foreground italic">"{user.archive_reason}"</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => openRestoreDialog(user, 'user')}
                            className="gap-1"
                          >
                            <RotateCcw className="h-4 w-4" />
                            Restore
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore {restoreType === 'participant' ? 'Participant' : 'User'}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to restore <strong>{itemToRestore?.first_name} {itemToRestore?.last_name}</strong>?</p>
            <p className="text-sm text-muted-foreground mt-2">
              This will make them visible in active lists again.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRestore}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivedPage;
